import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const JobView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Mock data - replace with actual API call
  const job = {
    id: 'JOB-001',
    title: 'Senior Frontend Developer',
    shortDescription: 'Build amazing user interfaces with React and TypeScript for our cutting-edge SaaS platform.',
    fullDescription: `
      <h2>About the Role</h2>
      <p>We are looking for a skilled Senior Frontend Developer to join our dynamic team. You will be responsible for developing and maintaining high-quality web applications using modern technologies.</p>
      
      <h3>Responsibilities:</h3>
      <ul>
        <li>Develop new user-facing features using React.js</li>
        <li>Build reusable components and front-end libraries for future use</li>
        <li>Translate designs and wireframes into high-quality code</li>
        <li>Optimize components for maximum performance across devices and browsers</li>
        <li>Collaborate with back-end developers and web designers</li>
      </ul>
      
      <h3>Requirements:</h3>
      <ul>
        <li>5+ years of experience in frontend development</li>
        <li>Strong proficiency in JavaScript, including DOM manipulation</li>
        <li>Thorough understanding of React.js and its core principles</li>
        <li>Experience with popular React workflows (Redux, Context API)</li>
        <li>Familiarity with newer specifications of EcmaScript</li>
      </ul>
    `,
    category: 'Technology',
    location: 'New York, NY',
    company: 'TechCorp Inc.',
    employerId: 'EMP-001',
    contractType: 'full-time',
    workMode: 'hybrid',
    minSalary: 90000,
    maxSalary: 130000,
    status: 'active',
    applications: 45,
    clicks: 234,
    views: 1567,
    featured: true,
    urgent: false,
    redirectUrl: '',
    createdAt: '2024-01-10',
    expiresAt: '2024-02-10',
    updatedAt: '2024-01-15'
  };

  const applications = [
    { id: 1, candidateName: 'Sarah Johnson', appliedDate: '2024-01-12', status: 'Under Review', matchScore: 92 },
    { id: 2, candidateName: 'Michael Chen', appliedDate: '2024-01-11', status: 'Interview', matchScore: 88 },
    { id: 3, candidateName: 'Emily Davis', appliedDate: '2024-01-10', status: 'Rejected', matchScore: 76 },
    { id: 4, candidateName: 'Robert Wilson', appliedDate: '2024-01-09', status: 'Under Review', matchScore: 95 },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      closed: { color: 'bg-red-100 text-red-800', label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const getApplicationStatusBadge = (status) => {
    const statusConfig = {
      'Under Review': { color: 'bg-yellow-100 text-yellow-800', icon: 'fas fa-clock' },
      'Interview': { color: 'bg-blue-100 text-blue-800', icon: 'fas fa-calendar' },
      'Rejected': { color: 'bg-red-100 text-red-800', icon: 'fas fa-times' },
      'Accepted': { color: 'bg-green-100 text-green-800', icon: 'fas fa-check' }
    };
    const config = statusConfig[status] || statusConfig['Under Review'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <i className={`${config.icon} mr-1`}></i>
        {status}
      </span>
    );
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatSalary = (min, max) => {
    return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
  };

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Job Detail | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/hpanel/jobs')}
              className="text-gray-600 hover:text-gray-800"
            >
              <i className="fas fa-arrow-left text-xl"></i>
            </button>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <h2 className="text-2xl font-bold text-white">{job.title}</h2>
                {job.featured && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                    <i className="fas fa-star mr-1"></i> Featured
                  </span>
                )}
                {job.urgent && (
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                    <i className="fas fa-exclamation mr-1"></i> Urgent
                  </span>
                )}
              </div>
              <p className="text-gray-400">{job.company} • {job.location} • {job.id}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/hpanel/jobs/edit/${job.id}`}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
            >
              <i className="fas fa-edit mr-2"></i>
              Edit Job
            </Link>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center">
              <i className="fas fa-chart-bar mr-2"></i>
              Analytics
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Job Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Information Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Company</label>
                <p className="text-gray-900">{job.company}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Location</label>
                <p className="text-gray-900">{job.location}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="text-gray-900">{job.category}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Contract Type</label>
                <p className="text-gray-900">{job.contractType.charAt(0).toUpperCase() + job.contractType.slice(1)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Work Mode</label>
                <p className="text-gray-900">{job.workMode.charAt(0).toUpperCase() + job.workMode.slice(1)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Salary Range</label>
                <p className="text-gray-900 font-medium">{formatSalary(job.minSalary, job.maxSalary)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">{getStatusBadge(job.status)}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Expires On</label>
                <p className="text-gray-900">{new Date(job.expiresAt).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-500 mb-2 block">Short Description</label>
              <p className="text-gray-700">{job.shortDescription}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 mb-2 block">Full Description</label>
              <div 
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: job.fullDescription }}
              />
            </div>
          </div>

          {/* Recent Applications Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Applications</h3>
              <button className="text-primary-600 text-sm font-medium">View All ({job.applications})</button>
            </div>
            
            <div className="space-y-4">
              {applications.map((application) => (
                <div key={application.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      {application.candidateName.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{application.candidateName}</h4>
                      <p className="text-sm text-gray-600">Applied on {new Date(application.appliedDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getApplicationStatusBadge(application.status)}
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMatchScoreColor(application.matchScore)}`}>
                      <i className="fas fa-robot mr-1"></i>
                      {application.matchScore}%
                    </div>
                    <Link
                      to={`/hpanel/candidates/${application.id}`}
                      className="text-primary-600 hover:text-primary-800"
                    >
                      <i className="fas fa-eye"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Stats & Actions */}
        <div className="space-y-6">
          {/* Job Stats Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Performance</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-eye text-blue-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Total Views</span>
                </div>
                <span className="text-lg font-semibold text-gray-800">{job.views}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-mouse-pointer text-green-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Total Clicks</span>
                </div>
                <span className="text-lg font-semibold text-gray-800">{job.clicks}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="fas fa-file-alt text-purple-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Applications</span>
                </div>
                <span className="text-lg font-semibold text-gray-800">{job.applications}</span>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">
                    {((job.applications / job.views) * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600">Conversion Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
            
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-copy mr-3 text-gray-400"></i>
                Duplicate Job
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-pause mr-3 text-gray-400"></i>
                {job.status === 'active' ? 'Pause Job' : 'Activate Job'}
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center">
                <i className="fas fa-share-alt mr-3 text-gray-400"></i>
                Share Job
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center">
                <i className="fas fa-archive mr-3"></i>
                Archive Job
              </button>
            </div>
          </div>

          {/* AI Insights Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">AI Insights</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <i className="fas fa-lightbulb text-yellow-500 mt-1 mr-3"></i>
                <p className="text-sm text-gray-600">
                  This job has a high application rate compared to similar positions.
                </p>
              </div>
              <div className="flex items-start">
                <i className="fas fa-chart-line text-green-500 mt-1 mr-3"></i>
                <p className="text-sm text-gray-600">
                  Salary range is competitive for the location and experience level.
                </p>
              </div>
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-orange-500 mt-1 mr-3"></i>
                <p className="text-sm text-gray-600">
                  Consider extending the posting duration to attract more senior candidates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobView;